import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Mail } from 'lucide-react';
import ReportEmailDialog from './ReportEmailDialog';
import { buildReportShareEmailPayload } from './reportEmailApi';
import { getReportCatalogEntry } from './reportingCatalog';
import {
  REPORT_SHARE_CLEAR,
  REPORT_SHARE_MESSAGE,
  isReportShareMessage,
  normalizeReportShareContext,
} from './reportShareProtocol';
import { ReportingShareProvider } from './ReportingShareContext';

function ReportingShareBar({ activeService, activeReport, shareContext, onEmailClick, canEmail }) {
  const catalog = getReportCatalogEntry(activeService, activeReport);
  if (!activeService || !catalog) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 border-b border-gray-200 bg-white shrink-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{catalog.report_name}</p>
        <p className="text-xs text-gray-500">
          Platform reporting · {shareContext?.metrics?.length ? 'Summary ready to share' : 'Preview or generate to load summary'}
        </p>
      </div>
      <button
        type="button"
        onClick={onEmailClick}
        disabled={!canEmail}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed"
        title={canEmail ? 'Email this report' : 'Load a report summary before emailing'}
      >
        <Mail className="w-4 h-4" />
        Email report
      </button>
    </div>
  );
}

/**
 * Platform-level report email host for AgenticRCM / Agency Reporting Center.
 * - Listens for REPORT_SHARE_CONTEXT from embedded service iframes
 * - Provides React context for in-shell panels (ExecReport)
 * - Renders share bar + email dialog (single entry point)
 */
export default function ReportingEmailShell({
  organizationId,
  activeService,
  activeReport,
  children,
}) {
  const [shareContext, setShareContext] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const resetContext = useCallback(() => setShareContext(null), []);

  useEffect(() => {
    resetContext();
  }, [activeService, activeReport, resetContext]);

  useEffect(() => {
    const handler = (event) => {
      if (!isReportShareMessage(event)) return;
      if (event.data.type === REPORT_SHARE_CLEAR) {
        setShareContext(null);
        return;
      }
      const normalized = normalizeReportShareContext(event.data.context);
      if (normalized) setShareContext(normalized);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const catalog = useMemo(
    () => getReportCatalogEntry(activeService, activeReport),
    [activeService, activeReport],
  );

  const mergedContext = useMemo(() => {
    if (!catalog) return null;
    return {
      service_key: shareContext?.service_key || catalog.service_key,
      report_name: shareContext?.report_name || catalog.report_name,
      report_period: shareContext?.report_period || null,
      report_url: shareContext?.report_url || null,
      executive_report_id: shareContext?.executive_report_id || null,
      metrics: shareContext?.metrics || [],
    };
  }, [catalog, shareContext]);

  const canEmail = Boolean(organizationId && mergedContext?.report_name);
  const defaultSubject = mergedContext?.report_name
    ? `${mergedContext.report_name}${mergedContext.report_period ? ` — ${mergedContext.report_period}` : ''}`
    : 'RCM Report';

  return (
    <ReportingShareProvider onContextChange={setShareContext} onClear={resetContext}>
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <ReportingShareBar
          activeService={activeService}
          activeReport={activeReport}
          shareContext={shareContext}
          canEmail={canEmail}
          onEmailClick={() => setEmailOpen(true)}
        />
        <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
      </div>

      <ReportEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        defaultSubject={defaultSubject}
        title="Email report"
        buildPayload={() =>
          buildReportShareEmailPayload({
            organizationId: String(organizationId),
            context: mergedContext,
          })
        }
      />
    </ReportingShareProvider>
  );
}
